/**
 * Client-side `platform_call` executor (browser).
 *
 * Shogun's cloud agent proposes `{resource, method, arguments}` platform
 * actions. The Go tools worker stamps `safety` (from the seeded catalog) and a
 * stable `idempotency_key`, then routes the call over the bridge to this
 * browser's `device_id`. This executor runs the call through the user's REAL
 * authenticated SDK and returns the result — acts-as-user with the user's exact
 * permissions (sdk ADR-033). No backend token minting, no
 * impersonation.
 *
 * Security invariants:
 *  - Fail-closed: dispatch ONLY through the generated `DISPATCH_ALLOWLIST`.
 *    Any `{resource, method}` not in it → `{status:"error"}`, never a live call.
 *  - Identity (sdk ADR-033): the call executes under the
 *    passed-in authenticated `sdk` (the user's real session). Tenant/identity is NEVER
 *    read from `call.arguments` — a crafted org/account/user in the arguments
 *    cannot change who the call runs as. We do not construct a new SDK from
 *    arguments.
 *  - `safety` is server-stamped (from the frame); it is NEVER re-derived from
 *    the method name and a model-supplied safety is never trusted.
 *  - `arguments` may carry BYOK secrets — they are NEVER logged.
 *
 * Confirmation (app-shell ADR-019) is non-blocking: a
 * `destructive` call with no recorded approval returns `confirmation_required` immediately (kicking off the
 * confirm UI out-of-band) so the 9-minute retryable bridge activity never waits
 * on a human. Approval is recorded ONLY by the `confirm` callback resolving true
 * (a real user DOM action — the model cannot set it). The agent then re-issues
 * the identical call (same `idempotency_key`); with approval on record it
 * executes.
 *
 * Idempotency: results are cached by `idempotency_key`, so a bridge-activity
 * retry that re-sends the same key returns the cached result — never a double
 * apply.
 */

import { queryClient } from '../providers/queryClient';
import {
  DISPATCH_ALLOWLIST,
  DocsIndex,
  type LocalAgentSDK,
  type PlatformCallFrameParams,
  type PlatformCallResult,
} from '@localagent/sdk';

/**
 * Which cached reads a successful non-read dispatch invalidates
 * (app-shell ADR-017).
 *
 * Every dispatchable resource that can write appears HERE or in
 * `RESOURCES_WITH_NO_CACHED_SURFACE`; a resource in neither fails
 * `platformCallExecutor.invalidation.test.ts`, so a newly dispatchable resource
 * cannot default to a silent no-op. A read-classified call still invalidates
 * nothing.
 */
export const INVALIDATION_KEYS_BY_RESOURCE: Record<string, string[][]> = {
  account: [['account']],
  accountManagement: [['org-roster']],
  agentProcesses: [['agent-processes'], ['agent-process-step-templates']],
  'agentProcesses.steps': [['agent-processes'], ['agent-process-step-templates']],
  billing: [['billing'], ['account', 'product-onboarding']],
  'context.profile': [['user-profile']],
  'context.sources': [['context-sources'], ['context-sources-health']],
  integrations: [['integrations']],
  mcp: [['mcp-servers'], ['mcp-tools'], ['tool-catalog']],
  orgChart: [['orgchart']],
  persona: [
    ['agent-definitions'],
    ['agent-definition'],
    ['agent-definition-by-id'],
    ['agent-definition-config'],
    ['agent-definition-config-by-id'],
    ['agent-groups'],
    ['agent-options'],
    ['system-agents'],
    ['providers'],
    ['provider-models'],
    ['provider-credential'],
    ['param-schema'],
    ['entry-references'],
    ['default-overrides'],
  ],
  reasoning: [['reasoning'], ['sessions']],
  sessions: [['sessions'], ['work']],
  sessionList: [['sessions'], ['session-list-pages'], ['session-search-pages']],
  secrets: [['account-secrets']],
  skills: [['account-skills']],
  support: [['support']],
  tools: [['tool-catalog'], ['mcp-servers'], ['mcp-tools']],
  user: [['user']],
  workManagement: [['work']],
  workspaces: [['workspaces'], ['workspace-projection'], ['workspace-operation-history']],
};

/**
 * A writable dispatchable resource this app renders from something other than a
 * react-query cache, so there is nothing to invalidate. The stated reason is the
 * point: it separates "surveyed, nothing cached" from "nobody looked".
 */
export const RESOURCES_WITH_NO_CACHED_SURFACE: Record<string, string> = {
  admin: 'the admin screens fetch imperatively and hold no query cache',
  automation: 'no automation surface is rendered by this app',
  device: 'device identity lives in jotai atoms, not a cached read',
  errors: 'the error list is fetched imperatively in Settings',
  events: 'ingested events are never read back through a cached query',
  feed: 'LiveFeed is SDK-owned state, not a react-query cache (app-shell ADR-025)',
  media: 'ArtifactsView fetches the catalog imperatively and holds no query cache',
  notifications: 'notification state is atoms fed by bridge push frames',
  search: 'search results are fetched per keystroke, never cached by key',
};

function invalidateForDispatch(resource: string): void {
  for (const queryKey of INVALIDATION_KEYS_BY_RESOURCE[resource] ?? []) {
    void queryClient.invalidateQueries({ queryKey });
  }
  if (resource === 'workManagement') {
    // Session LISTS only: a work write can mint a session the sessions catalog
    // holder also hears about; this keeps the this-tab dispatch path instant.
    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'sessions' && typeof query.queryKey[2] !== 'string',
    });
  }
}

export interface PlatformCallPreview {
  resource: string;
  method: string;
  arguments: Record<string, unknown>;
}

export type ConfirmFn = (preview: PlatformCallPreview) => Promise<boolean>;

export interface PlatformCallExecutor {
  execute(call: PlatformCallFrameParams): Promise<PlatformCallResult>;
}

const sharedDocsIndex = new DocsIndex();

/**
 * Dispatch an allowlisted `platform_call` through the authenticated SDK — the
 * EXECUTION half shared by the web executor (`createPlatformCallExecutor`) and
 * the desktop renderer consumer (`useWebBridgeClient` desktop arm):
 *
 *  - Fail-closed: rejects any `{resource,method}` not in `DISPATCH_ALLOWLIST`.
 *  - Identity (sdk ADR-033): runs under the passed-in `sdk`
 *    only — never reads tenant/identity from `arguments`.
 *  - Positional args mapped by the resolved `argSchema`: a single-object
 *    body flattens (`arguments` → the SDK `params` positional arg) so the agent
 *    never has to know the internal `params` name; named scalars map in
 *    signature order. A missing REQUIRED field is a loud typed error naming the
 *    field (no-fallbacks) — `undefined` never reaches the SDK.
 *  - Backend errors surfaced verbatim — no fabricated success.
 *
 * It does NOT gate destructive confirms or de-dupe idempotency — those are the
 * caller's concern (web: the executor's own gate; desktop: the daemon, which
 * only forwards already-gated, already-approved calls).
 */
/**
 * Resolve a possibly-dotted resource path against the SDK (e.g. "personas" →
 * `sdk.personas`, "agentProcesses.steps" → `sdk.agentProcesses.steps`). Returns
 * undefined if any segment is missing, so the caller surfaces "method not
 * permitted" loudly rather than throwing.
 */
function resolveResourcePath(
  sdk: LocalAgentSDK,
  resourcePath: string,
): Record<string, unknown> | undefined {
  let cur: unknown = sdk;
  for (const seg of resourcePath.split('.')) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur !== null && typeof cur === 'object'
    ? (cur as Record<string, unknown>)
    : undefined;
}

export function refuseDispatch(key: string): string {
  if (key.startsWith('auth.')) {
    return (
      `method not permitted: ${key} is not agent-reachable. ` +
      'Create an organization with accountManagement.createOrganization; ' +
      'that does not switch this session.'
    );
  }
  return `method not permitted: ${key}`;
}

export async function dispatchAllowlistedCall(
  sdk: LocalAgentSDK,
  call: { resource: string; method: string; arguments: Record<string, unknown> },
): Promise<PlatformCallResult> {
  const key = `${call.resource}.${call.method}`;
  const allowlisted = DISPATCH_ALLOWLIST[key];
  if (allowlisted === undefined) {
    return { status: 'error', error: refuseDispatch(key) };
  }
  const resource = resolveResourcePath(sdk, call.resource);
  const fn = resource?.[call.method];
  if (typeof fn !== 'function') {
    return { status: 'error', error: refuseDispatch(key) };
  }
  const methodDocs = sharedDocsIndex.method(key);
  const argSchema = methodDocs?.argSchema;
  if (argSchema === undefined) {
    return { status: 'error', error: 'no argument schema for method' };
  }

  const args = call.arguments ?? {};

  // Every REQUIRED field must be supplied — a missing one is a loud,
  // typed error naming the field, never `undefined` silently reaching the SDK.
  for (const field of argSchema.required ?? []) {
    if (args[field] === undefined) {
      return { status: 'error', error: `missing required field '${field}' for ${key}` };
    }
  }

  // A single-object-body method takes the whole agent object as its one
  // positional `params` arg; otherwise map named params in signature order.
  const orderedArgs =
    argSchema['x-arg-style'] === 'body'
      ? [args]
      : Object.keys(argSchema.properties).map((name) => args[name]);
  try {
    const result = await (fn as (...args: unknown[]) => Promise<unknown>).apply(
      resource,
      orderedArgs,
    );
    // Here, not in `createPlatformCallExecutor`: the desktop arm of
    // `useWebBridgeClient` calls this function directly, so an executor-level
    // invalidation would leave the desktop dock non-refreshing
    // (app-shell ADR-017).
    if (allowlisted.safety !== 'read') {
      invalidateForDispatch(call.resource);
    }
    return { status: 'ok', result };
  } catch (err) {
    return { status: 'error', error: err instanceof Error ? err.message : String(err) };
  }
}

export function createPlatformCallExecutor(
  sdk: LocalAgentSDK,
  confirm: ConfirmFn,
): PlatformCallExecutor {
  const resultCache = new Map<string, PlatformCallResult>();
  const approvals = new Set<string>();
  const pendingConfirms = new Set<string>();

  async function execute(call: PlatformCallFrameParams): Promise<PlatformCallResult> {
    const key = `${call.resource}.${call.method}`;

    const cached = resultCache.get(call.idempotency_key);
    if (cached !== undefined) {
      return cached;
    }

    const entry = DISPATCH_ALLOWLIST[key];
    if (entry === undefined) {
      return { status: 'error', error: refuseDispatch(key) };
    }

    // Non-blocking destructive confirm (app-shell ADR-019). Fail-safe:
    // gate if EITHER the client's own authoritative allowlist entry OR the
    // server-stamped frame says
    // destructive. The bundled `entry.safety` is generated from the same catalog
    // and cannot be influenced over the wire, so a missing/unknown/tampered frame
    // `safety` can never fail open (unclassified is destructive).
    const isDestructive = entry.safety === 'destructive' || call.safety === 'destructive';
    // A server-stamped pre-authorization completes a destructive
    // direct call without a chat-thread confirmation. `pre_authorized` is
    // authoritative (set only by the Go worker from a TRUSTED
    // DirectInvocationOverride, never from model arguments — same trust level as
    // `safety`), so honoring it here cannot be triggered by the agent path.
    if (isDestructive && call.pre_authorized !== true && !approvals.has(call.idempotency_key)) {
      const preview: PlatformCallPreview = {
        resource: call.resource,
        method: call.method,
        arguments: call.arguments,
      };
      if (!pendingConfirms.has(call.idempotency_key)) {
        pendingConfirms.add(call.idempotency_key);
        void confirm(preview).then(
          (approved) => {
            pendingConfirms.delete(call.idempotency_key);
            if (approved === true) {
              approvals.add(call.idempotency_key);
            }
          },
          () => {
            pendingConfirms.delete(call.idempotency_key);
          },
        );
      }
      return { status: 'confirmation_required', preview };
    }

    const result = await dispatchAllowlistedCall(sdk, call);
    if (result.status === 'ok') {
      resultCache.set(call.idempotency_key, result);
    }
    return result;
  }

  return { execute };
}
